import React, { useState, useRef, useEffect } from 'react';

interface CheckboxSelectProps {
  titulo: string;
  placeholder: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
}

const CheckboxSelect: React.FC<CheckboxSelectProps> = ({
  titulo,
  placeholder,
  value,
  onChange,
  options
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      // Remove se já estiver selecionado
      onChange(value.filter(item => item !== optionValue));
    } else {
      // Adiciona se não estiver selecionado
      onChange([...value, optionValue]);
    }
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  const handleRemoveChip = (optionValue: string) => {
    onChange(value.filter(item => item !== optionValue));
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    return ''; // Não mostra texto quando há chips
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--c2d3748)',
        marginBottom: '0.5rem',
        display: 'block'
      }}>
        {titulo}
      </label>
      
      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Campo principal */}
        <div
          onClick={handleInputClick}
          style={{
            width: '100%',
            minHeight: '48px',
            padding: value.length > 0 ? '8px 1rem' : '0 1rem',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: value.length > 0 ? 'flex-start' : 'center',
            backgroundColor: 'var(--surface-card)',
            color: value.length === 0 ? 'var(--ca0aec0)' : 'var(--c2d3748)',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          {/* Chips dos itens selecionados */}
          {value.length > 0 && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px', 
              flex: 1,
              alignItems: 'center'
            }}>
              {value.map((selectedValue) => {
                const option = options.find(opt => opt.value === selectedValue);
                const label = option ? option.label : selectedValue;
                return (
                  <div
                    key={selectedValue}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'var(--cf7fafc)',
                      border: '1px solid var(--ce2e8f0)',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      color: 'var(--c4a5568)',
                      gap: '6px'
                    }}
                    onClick={(e) => e.stopPropagation()} // Evita abrir o dropdown ao clicar no chip
                  >
                    <span>{label}</span>
                    <button
                      onClick={() => handleRemoveChip(selectedValue)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--c718096)',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#e53e3e';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#718096';
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Texto placeholder quando não há seleções */}
          {value.length === 0 && (
            <span style={{ flex: 1 }}>{getDisplayText()}</span>
          )}
          
          {/* Seta do dropdown */}
          <div style={{
            color: 'var(--c718096)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            alignSelf: value.length > 0 ? 'flex-start' : 'center',
            marginTop: value.length > 0 ? '8px' : '0'
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 8L2 4h8L6 8z" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            overflow: 'hidden'
          }}>
            {/* Lista de opções com checkboxes */}
            <div>
              {options.map((option, index) => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={index}
                    onClick={() => handleToggle(option.value)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: 'var(--c2d3748)',
                      borderBottom: index < options.length - 1 ? '1px solid var(--cf7fafc)' : 'none',
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Checkbox no estilo do sistema */}
                    <div style={{
                      width: '22px',
                      height: '22px',
                      border: `2px solid ${isSelected ? 'var(--ce52940)' : 'var(--c798891)'}`,
                      borderRadius: '3px',
                      backgroundColor: isSelected ? 'var(--ce52940)' : 'var(--surface-input)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}>
                      {isSelected && (
                        <span style={{
                          color: 'var(--ink-white)',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          opacity: 1,
                          transition: 'opacity 0.2s'
                        }}>
                          ✔
                        </span>
                      )}
                    </div>
                    
                    {/* Label */}
                    <span style={{ flex: 1 }}>{option.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckboxSelect;
